import _ from "lodash";
import React, { Fragment, useContext, useEffect, useState, useCallback, } from "react";
import { confirmAlert } from "react-confirm-alert";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, UserContext, helpers } from "../utils";
import { AttachmentManager, ItemTypeahead, ServiceThreadSpinner, VendorTypeahead } from "./";
import classNames from "classnames";

const DEFAULT_VALIDATIONS = {
   allowZeroCost: false,
   currencyDecimals: 2,
   currentCostMessage: "",
   listPrice: true, // Required for FG items and optional for IP items
   minOrderQty: true, // Min Order Qty is only required for FG items
   pkgType: false, // Packaging Type is only for package items of certain inventory classes
   pmSite: false, // Default location(s) for PM items
   rangePackageTare: false, // FSUP/SSUP packaging classes have values in the double range
   siteId: true, // Default location for non-PM items
   standardCostMessage: "",
   standardNetWeight: true, // Std Net Weight is required for FG/IP items
   tareNormalRange: true, // FG/IP items have integer ranges
   tareWeight: true, // Tare Weight is required for FG/IP items and packaging items in the FSUP/SSUP classes
   tareWeightMaxLength: 3,
   unitsPerSku: true, // Units Per Sku is required for FG/IP items
   vendorId: false, // Only required for PM items
   wasteFactor: false, // Waste Percent is for in process or purchased material items
};

const bigLink = {
   color: "#F26723",
   fontSize: "1.5rem",
   textDecoration: "underline",
};

function AttachmentRow({ displayId }) {
   return (
      <Row>
         <Col>
            <Card className="dashboard-card">
               <CardBody>
                  <AttachmentManager
                     attachmentTypeId={constants.ATTACHMENT_TYPES.NewItemRequest}
                     contextName={displayId}
                     attachmentKey={displayId}
                  />
               </CardBody>
            </Card>
         </Col>
      </Row>
   );
}

function ColPrompt({ title }) {
   return (
      <Col xs="3" className="text-white">
         <h5 className="float-end">{title}:</h5>
      </Col>
   );
}

function ColValue(props) {
   return (
      <Col xs="6" className="text-white">
         {props.children}
      </Col>
   );
}

function InitiateRequest({ referenceItem, setReferenceItem, newItem, setNewItem, onStartRequest, }) {
   return (
      <Fragment>
         <Row>
            <Col xs={{ offset: 3, size: 9 }} className="text-white pt-2">
               <p className="my-1">
                  Use the Reference Item field only for existing items you wish to use
                  as base information for the new item.
               </p>
               <p>
                  Use the Item Comment field to enter information about the new item
                  including any item code suggestions.
               </p>
            </Col>
         </Row>
         <Row className="py-2">
            <ColPrompt title="Reference Item" />
            <Col xs={9}>
               <ItemTypeahead
                  value={referenceItem}
                  isClearable
                  placeholder="Select an EXISTING sample/item number or leave blank"
                  includeSampleItems
                  onChange={(e) => setReferenceItem(e)}
               />
            </Col>
         </Row>
         <Row>
            <ColPrompt title="Item Comment" />
            <Col xs={9}>
               <Input
                  className="serviceThreadTextArea"
                  type="textarea"
                  value={newItem?.itemComment}
                  maxLength={140}
                  style={{ height: "220px" }}
                  onChange={(e) => setNewItem({ ...newItem, itemComment: e.target.value })}
               />
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <Col>
               <Button
                  title="Start Request"
                  className="shredButton float-end ms-2"
                  onClick={onStartRequest}
               >
                  Start Request
               </Button>
            </Col>
         </Row>
      </Fragment>
   );
}

function ItemDescriptionHeader({ itemWithDesc, itemClass, itemClasses, onChange, comment, }) {
   const notProvided = itemWithDesc === "Not Provided";
   return (
      <Fragment>
         <Row className="py-2">
            <ColPrompt title="Reference Item" />
            <Col xs={9} className="text-white">
               <h5 className={classNames("", { "text-brightRed": notProvided })}>
                  {itemWithDesc}
               </h5>
            </Col>
         </Row>
         <Row>
            <Col xxl={{ offset: 3, size: 6 }}>
               <h5 className="text-white">{comment}</h5>
            </Col>
         </Row>
         <hr />
         {itemClasses && itemClasses.length ? (
            <Row className="newProductionItemLine">
               <ColPrompt title="Item Class" />
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={itemClasses}
                     value={itemClass}
                     onChange={(e) => onChange("itemClass", e)}
                     isSearchable
                  />
               </Col>
            </Row>
         ) : null}
      </Fragment>
   );
}

function EditRequest({ editItem, siteList, threadColors, onChange, onCreateItem, setEditItem, referenceItem, }) {
   const { currentUser } = useContext(UserContext);
   const [validations, setValidations] = useState(DEFAULT_VALIDATIONS);
   const [prePopulateSpecification, setPrePopulateSpecification] = useState(
      editItem.prePopulateSpecification || false
   );
   const [prePopulateMachineSetups, setPrePopulateMachineSetups] = useState(
      editItem.prePopulateMachineSetups || false
   );

   useEffect(() => {
      let newValidations = Object.assign({}, DEFAULT_VALIDATIONS);
      if (editItem?.itemClass) {
         const currencyDecimals = editItem.itemClass.currencyDecimals;
         newValidations.standardCostMessage =
            "Standard Cost must be in format 9999.".padEnd(currencyDecimals, "9") + "\n";
         newValidations.currentCostMessage =
            "Current Cost must be in format 9999.".padEnd(currencyDecimals, "9") + "\n";
         newValidations.allowZeroCost = editItem.itemClass.isPurchasedMaterial;
         let newItem = Object.assign({}, editItem);
         if (editItem.itemClass.isFinishedGood) {
            newValidations.listPrice = true;
            newItem.shippingClass = constants.SHIPPING_CLASS.A;
         } else {
            newValidations.listPrice = false;
            newItem.shippingClass = null;
            newValidations.wasteFactor = true; // Waste factor is required for all IP & PM classes. Start with the default for the inventory class.
            newItem.wasteFactor = editItem.itemClass.wasteFactor * 100.0;
            if (editItem.itemClass.isPurchasedMaterial) {
               newValidations.listPrice = false; // No list price exists on items purchased from vendors
               newValidations.vendorId = true; // Purchased Material items need to be assigned to a vendor
               // Get the right type of location information
               newValidations.pmSite = true;
               newValidations.siteId = false;
               // These are optional for PM items
               newValidations.tareWeight = false;
               newValidations.standardNetWeight = false;
               newValidations.unitsPerSku = false;
               newValidations.minOrderQty = false;
            }
         }
         if (editItem.itemClass.isPkg) {
            // FSUP/SSUP classes need additional info not needed on other items
            newValidations.pkgType = true;
            newItem.pkgType = null;
            // we also need to get a tare weight - this is the one PM type that requires it
            newValidations.tareWeight = true;
            newValidations.rangePackageTare = true; // Double range specific to packages
            newValidations.tareNormalRange = false; // Integer range for all others
            newValidations.tareWeightMaxLength = 7; // To accomodate for decimals 0.0001 to 99.0000
         }
         setEditItem(newItem);
      }
      setValidations(newValidations);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editItem?.itemClass]);

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleInputChange = useCallback(
      (event, property, decimalPlaces = 2) => {
         const { value } = event.target;
         if (value === "") {
            onChange(property, value);
            return;
         }
         const regex = new RegExp(`^[+-]?(\\d+(\\.\\d{0,${decimalPlaces}})?)$`);

         const found = value.match(regex);
         if (found === null) {
            toast.warning(
               `your number ${value} is not formatted correctly please try again`
            );
            return;
         }
         onChange(property, value);
      }
   );

   function onSave() {
      let warnings = [];
      if (
         !currentUser?.isAdmin &&
         !currentUser?.isPowerUser &&
         !currentUser?.isEngineerEdit &&
         !currentUser?.isEngineerEdit &&
         !currentUser?.isInventoryEdit
      ) {
         warnings.push(
            "You have insufficient rights to create a new item. Contact your application administrator.\n"
         );
      }
      if (
         !editItem.itemDescription ||
         _.trim(editItem.itemDescription).length <= 2
      ) {
         warnings.push("Item description is required\n");
      }
      if (validations.pkgType && !editItem.pkgType) {
         warnings.push("Package type is required\n");
      }
      if (validations.wasteFactor && !editItem.wasteFactor) {
         warnings.push("Waste factor is required\n");
      }
      if (validations.listPrice && !editItem.listPrice) {
         warnings.push("List price is required\n");
      }
      if (validations.vendorId && !editItem.vendorId) {
         warnings.push("Vendor is required\n");
      }
      if (validations.minOrderQty && !editItem.minOrderQty) {
         warnings.push("Minimum order quantity is required\n");
      }
      const tareValue = parseFloat(editItem.tareWeight);
      if (validations.tareWeight && !tareValue) {
         warnings.push("Tare weight is required\n");
      }
      if (validations.tareNormalRange && (tareValue < 0.0 || tareValue > 999.0)) {
         warnings.push("Tare Weight must be whole number between 0 and 999\n");
      }
      if (validations.rangePackageTare && (tareValue < 0.0001 || tareValue > 99.0)) {
         warnings.push("Tare Weight must be whole number between 0.0001 and 99.0000\n");
      }
      if (validations.standardNetWeight && !editItem.standardNetWeight) {
         warnings.push("Standard net weight is required\n");
      }
      if (validations.unitsPerSku && !editItem.unitsPerSku) {
         warnings.push("Units are required\n");
      }
      if (validations.siteId && !editItem.siteId) {
         warnings.push("Site is required\n");
      }
      if (!validations.allowZeroCost && !editItem.standardCost) {
         warnings.push(validations.standardCostMessage);
      }
      if (!validations.allowZeroCost && !editItem.currentCost) {
         warnings.push(validations.currentCostMessage);
      }
      if (warnings.length) {
         toast.warning(warnings.join(''));
         return;
      }
      onCreateItem(
         editItem,
         editItem.itemClass,
         prePopulateSpecification,
         prePopulateMachineSetups
      );
   }

   return (
      <Fragment>
         <Row className={classNames("newProductionItemLine", { "d-none": !referenceItem, })}>
            <Col xs={{ size: 9, offset: 3 }} className="text-white">
               <Input
                  type="checkbox"
                  value={prePopulateSpecification}
                  className="me-3 ms-1"
                  onChange={() => {
                     const newValue = !prePopulateSpecification;
                     setPrePopulateSpecification(newValue);
                     onChange("prePopulateSpecification", newValue);
                  }}
               />
               Pre-populate the new specification with a copy of the Reference Item Values?
            </Col>
         </Row>
         <Row className={classNames("newProductionItemLine", { "d-none": !referenceItem, })}>
            <Col xs={{ size: 9, offset: 3 }} className="text-white">
               <Input
                  type="checkbox"
                  value={prePopulateMachineSetups}
                  className="me-3 ms-1"
                  onChange={() => {
                     const newValue = !prePopulateMachineSetups;
                     setPrePopulateMachineSetups(newValue);
                     onChange("prePopulateMachineSetups", newValue);
                  }}
               />
               Pre-populate machine setups with a copy of the Reference Item setup(s)?
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Item" />
            <Col xs="6" className="text-white">
               To be determined by class selected and provided upon saving the new item
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Item Description" />
            <ColValue>
               <Input
                  type="text"
                  maxLength={25}
                  value={editItem.itemDescription || ""}
                  onChange={(e) => onChange("itemDescription", e.target.value)}
               />
            </ColValue>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Color" />
            <Col xs="6">
               <Select
                  classNamePrefix="serviceThreadSelect"
                  options={threadColors}
                  value={_.find(threadColors, (x) => x.label === editItem.color)} //640
                  onChange={(e) => onChange("color", e.label)}
                  isSearchable
               />
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Standard Cost" />
            <ColValue>
               <Input
                  type="text"
                  value={editItem?.standardCost}
                  onChange={(e) => handleInputChange(e, "standardCost")}
               />
            </ColValue>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Current Cost" />
            <ColValue>
               <Input
                  type="text"
                  value={editItem?.currentCost}
                  onChange={(e) => handleInputChange(e, "currentCost")}
               />
            </ColValue>
         </Row>
         {editItem.itemClass.isFinishedGood || editItem.itemClass.isInProcess ? (
            <Row className="newProductionItemLine">
               <ColPrompt title="List Price" />
               <ColValue>
                  <Input
                     type="text"
                     value={editItem?.listPrice}
                     onChange={(e) => handleInputChange(e, "listPrice")}
                  />
               </ColValue>
            </Row>
         ) : null}
         <Row className="newProductionItemLine">
            <ColPrompt title="Shipping Class" />
            <RadioOption
               classType={constants.SHIPPING_CLASS.A}
               currentValue={editItem.shippingClass}
               onChange={onChange}
               fieldName="shippingClass"
            />
            <RadioOption
               classType={constants.SHIPPING_CLASS.B}
               currentValue={editItem.shippingClass}
               onChange={onChange}
               fieldName="shippingClass"
            />
            <RadioOption
               classType={constants.SHIPPING_CLASS.C}
               currentValue={editItem.shippingClass}
               onChange={onChange}
               fieldName="shippingClass"
            />
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Tare Weight" />
            <Col xs="6">
               <Input
                  type="text"
                  maxLength={validations.tareWeightMaxLength}
                  value={editItem?.tareWeight}
                  onChange={(e) => onChange("tareWeight", e.target.value)}
               />
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Std Net Weight" />
            <Col xs="6">
               <Input
                  type="text"
                  value={editItem?.standardNetWeight}
                  onChange={(e) => handleInputChange(e, "standardNetWeight", 0)}
               />
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Units Per Sku" />
            <Col xs="6">
               <Input
                  type="number"
                  value={editItem.unitsPerSku}
                  onChange={(e) => handleInputChange(e, "unitsPerSku", 0)}
               />
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="Minimum Order Qty" />
            <Col xs="6">
               <Input
                  type="number"
                  value={editItem.minOrderQty || ""}
                  onChange={(e) => onChange("minOrderQty", e.target.value)}
               />
            </Col>
         </Row>
         {editItem.itemClass.isPurchasedMaterial ? (
            <Row className="newProductionItemLine">
               <ColPrompt title="Site ID PM" />
               <Col xs="6">
                  <Row className="mb-2">
                     <ColPrompt title="PLT" />
                     <Col xs="9">
                        <Input
                           type="text"
                           maxLength={50}
                           value={editItem.plant || ""}
                           onChange={(e) => onChange("plant", e.target.value)}
                        />
                     </Col>
                  </Row>
                  <Row>
                     <ColPrompt title="PLTWHSE" />
                     <Col xs="9">
                        <Input
                           type="text"
                           maxLength={25}
                           value={editItem.warehouse || ""}
                           onChange={(e) => onChange("warehouse", e.target.value)}
                        />
                     </Col>
                  </Row>
               </Col>
            </Row>
         ) : (
            <Row className="newProductionItemLine">
               <ColPrompt title="Site ID" />
               <Col xs="6">
                  <Select
                     classNamePrefix="serviceThreadSelect"
                     options={siteList}
                     value={_.find(siteList, (x) => x.value === editItem.siteId)}
                     onChange={(e) => onChange("siteId", e.value)}
                     isSearchable
                  />
               </Col>
            </Row>
         )}
         <Row className="newProductionItemLine">
            <ColPrompt title="Bin Location" />
            <Col xs="6">
               <Input
                  type="text"
                  maxLength={25}
                  value={editItem.binLocation || ""}
                  onChange={(e) => onChange("binLocation", e.target.value)}
               />
            </Col>
         </Row>
         <Row className="newProductionItemLine">
            <ColPrompt title="ABC Code" />
            <RadioOption
               classType={constants.ABC_CODE.A}
               currentValue={editItem.abcCode}
               onChange={onChange}
               fieldName="abcCode"
               abc
            />
            <RadioOption
               classType={constants.ABC_CODE.B}
               currentValue={editItem.abcCode}
               onChange={onChange}
               fieldName="abcCode"
               abc
            />
            <RadioOption
               classType={constants.ABC_CODE.C}
               currentValue={editItem.abcCode}
               onChange={onChange}
               fieldName="abcCode"
               abc
            />
         </Row>
         {editItem.itemClass.isFinishedGood ? null : (
            <Row className="newProductionItemLine">
               <ColPrompt title="Waste %" />
               <Col xs="6">
                  <Input
                     id="wasteFactor"
                     type="text"
                     value={editItem.wasteFactor}
                     onChange={(e) => {
                        if (_.isEmpty(e.target.value)) {
                           onChange("wasteFactor", "");
                        } else if (helpers.validateNumber(e, 2)) {
                           onChange("wasteFactor", e.target.value);
                        } else {
                           toast.warning(
                              `Your number ${e?.target.value} is not formatted correctly please try again`
                           );
                        }
                     }}
                  />
               </Col>
            </Row>
         )}
         {editItem.itemClass.isPkg ? (
            <Row className="newProductionItemLine">
               <ColPrompt title="Packaging Type" />
               <Col xs="3">
                  <Label style={{ color: "white", fontSize: "1.25rem" }}>
                     <Input
                        className="me-2"
                        type="radio"
                        name="pkgType"
                        value={editItem.pkgType === constants.PKG_TYPE.CONE_TUBE_SPOOL}
                        onClick={() => onChange("pkgType", constants.PKG_TYPE.CONE_TUBE_SPOOL)}
                     />
                     Cone/Tube/Spool
                  </Label>
               </Col>
               <Col xs="3">
                  <Label check style={{ color: "white", fontSize: "1.25rem" }}>
                     <Input
                        className="me-2"
                        type="radio"
                        name="pkgType"
                        value={editItem.pkgType === constants.PKG_TYPE.BOX_DIVIDER}
                        onClick={() => onChange("pkgType", constants.PKG_TYPE.BOX_DIVIDER)}
                     />
                     Box/Divider
                  </Label>
               </Col>
            </Row>
         ) : null}
         {editItem.itemClass.isPurchasedMaterial ? (
            <Fragment>
               <Row className="newProductionItemLine">
                  <ColPrompt title="Vendor" />
                  <Col xs="6">
                     <VendorTypeahead
                        value={editItem.vendor}
                        onChange={(selection) => onChange("vendorId", selection)}
                        debounceTimeout={300}
                     />
                  </Col>
               </Row>
               <Row className="newProductionItemLine">
                  <ColPrompt title="Vendor Item" />
                  <Col xs="6">
                     <Input
                        type="text"
                        maxLength={50}
                        value={editItem.vendorItem || ""}
                        onChange={(e) => onChange("vendorItem", e.target.value)}
                     />
                  </Col>
               </Row>
               <Row className="newProductionItemLine">
                  <ColPrompt title="Vendor Item Desc" />
                  <Col xs="6">
                     <Input
                        type="text"
                        maxLength={50}
                        value={editItem.vendorItemDescription || ""}
                        onChange={(e) => onChange("vendorItemDescription", e.target.value)}
                     />
                  </Col>
               </Row>
               <Row className="newProductionItemLine">
                  <ColPrompt title="Vendor Price" />
                  <Col xs="6">
                     <Input
                        type="text"
                        value={editItem?.vendorPrice}
                        onChange={(e) => handleInputChange(e, "vendorPrice")}
                     />
                  </Col>
               </Row>
               <Row className="newProductionItemLine">
                  <ColPrompt title="Vendor UofM" />
                  <Col xs="6">
                     <Input
                        type="text"
                        maxLength={25}
                        value={editItem.vendorUnitOfMeasure || ""}
                        onChange={(e) => onChange("vendorUnitOfMeasure", e.target.value)}
                     />
                  </Col>
               </Row>
            </Fragment>
         ) : null}
         <Row>
            <Col>
               <Button
                  title="createButton"
                  className="shredButton float-end ms-2"
                  onClick={onSave}
               >
                  Create Item
               </Button>
            </Col>
         </Row>
      </Fragment>
   );
}

function RadioOption({ onChange, currentValue, classType, fieldName, abc }) {
   const value = abc ? classType.value : classType;
   return (
      <Col xs="2" className="text-white">
         <Label>
            <Input
               type="radio"
               name={fieldName}
               defaultChecked={currentValue === value}
               onClick={() => onChange(fieldName, classType)}
            />
         </Label>
         <span className="ms-2">
            {classType.label ? classType.label : classType}
         </span>
      </Col>
   );
}

function RequestHeader({ displayId, requestedByFullName, createDate }) {
   return (
      <Fragment>
         <Row>
            <ColPrompt title="Request Id" />
            <Col xs={6} className="text-white">
               <h5>{displayId ? displayId : "Determined upon save"}</h5>
            </Col>
         </Row>
         <Row>
            <ColPrompt title="Requested By" />
            <Col xs={6} className="text-white">
               <h5>
                  {requestedByFullName} on {createDate}
               </h5>
            </Col>
         </Row>
      </Fragment>
   );
}

function ReviewRequest({ reviewItem, currentUserisReviewer }) {
   const navigate = useNavigate();
   const BOM_EDIT_STEP = {
      prompt: "Click here to go to the BOM Edit Screen",
      url: `/bom-components/search?bomComponent=${JSON.stringify({
         value: reviewItem.itemNumber,
         label: reviewItem.itemWithDescription,
      })}`,
      itemNumber: reviewItem.itemNumber,
      itemWithDescription: reviewItem.itemWithDescription,
   };
   const [rejectionReason, setRejectionReason] = useState("");
   const specIsPM = reviewItem.specNumber.startsWith("PM");
   let nextStep = null;

   function doReject() {
      api
         .post("Item/ApproveOrDeny", {
            ItemRequestId: reviewItem.id,
            FinalApproval: false,
            RejectedComment: rejectionReason,
         })
         .then((r) => {
            if (r.data.success) {
               handleRefresh();
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   const handleRefresh = () => {
      navigate(0);
   };

   function onApprove() {
      api
         .post("Item/ApproveOrDeny", {
            ItemRequestId: reviewItem.id,
            FinalApproval: true,
            ItemNumber: reviewItem.itemNumber,
         })
         .then((r) => {
            if (r.data.success) {
               handleRefresh();
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   function onReject() {
      if (!rejectionReason || !rejectionReason.length) {
         toast.warning("Provide a rejection reason in order to reject");
         return;
      }
      if (reviewItem.rejectedBy) {
         confirmAlert({
            title: "Replacing Rejection",
            message: "This will replace a previous rejection. Continue?",
            buttons: [
               { label: "Yes", onClick: doReject, },
               { label: "No", },
            ],
         });
      } else {
         doReject();
      }
   }

   function replacer(key, value) {
      // Encode # symbol in item string for inventory dashboard link
      if (typeof value === 'string')
         return value.replace(/#/g, '%23');

      return value;
   }

   const reviewItemSelect = JSON.stringify({
      value: reviewItem.itemNumber,
      label: reviewItem.itemWithDescription,
   });

   if (reviewItem.isActive) {
      if ((reviewItem.bomSetupBy && reviewItem.specSetupBy) || reviewItem.rejectedBy) {
         // Do nothing. Final approval is required or the request has been rejected, so next step is on this page anyway
      } else if (reviewItem.machineSetupBy && reviewItem.machineSetupRequired) {
         nextStep = BOM_EDIT_STEP;
      } else if (reviewItem.specSetupBy) {
         // meaning that specifcation has already been finished
         if (specIsPM) {
            // Machine setups and BOM's are not required for purchased materials.
            // Final approval is the next required step which is on this page anyway.
         } else if (!reviewItem.machineSetupRequired) {
            nextStep = BOM_EDIT_STEP;
         } else {
            // show link to MSR screen here
            nextStep = {
               prompt: "Click here to go to the Machine Setup Screen",
               url: `/machine-setups/${reviewItemSelect}`,
            };
         }
      } else if (reviewItem.itemSetupBy) {
         // show link to Spec EDIT screen here
         nextStep = {
            prompt: "Click here to go to the Specification Edit Screen",
            url: `/quality-specifications/${reviewItemSelect}`,
         };
      }
   }

   return (
      <Fragment>
         <Row className="pt-2">
            <Col xxl={3} className="text-end text-white">
               <h5>Reference Item:</h5>
            </Col>
            <Col>
               {reviewItem.referenceItemString === "Not provided" ? (
                  <h5 className="text-white ">Not Provided</h5>
               ) : (
                  <a rel="noreferrer"
                     target="_blank"
                     style={bigLink}
                     href={`/inventoryDashboard/${JSON.stringify({
                        value: reviewItem.referenceItem,
                        label: reviewItem.referenceItemString,
                     }, replacer)}`}
                  >
                     {reviewItem.referenceItemString}
                  </a>
               )}
            </Col>
         </Row>
         <Row style={{ color: "white" }}>
            <Col xxl={{ offset: 3, size: 6 }}>
               <h5 className="text-start">{reviewItem.itemComment}</h5>
            </Col>
         </Row>
         <hr className="my-2" />
         <Row className="mb-5">
            <ColPrompt title="Item" />
            <ColValue>
               <span>{reviewItem.itemWithDescription}</span>
            </ColValue>
         </Row>
         {reviewItem.itemSetupBy ? (
            <Row>
               <ColPrompt title="Item Setup" />
               <ColValue>
                  <Row>
                     <Col xs="6">
                        <span>Date:</span> {reviewItem.itemSetupDate}
                     </Col>
                     <Col xs="6">Completed By: {reviewItem.itemSetupBy}</Col>
                  </Row>
               </ColValue>
            </Row>
         ) : null}
         {reviewItem.specSetupBy ? (
            <Row>
               <ColPrompt title="Specification Setup" />
               <ColValue>
                  <Row>
                     <Col xs="6">
                        <span>Date:</span> {reviewItem.specSetupDate}
                     </Col>
                     <Col xs="6">Completed By: {reviewItem.specSetupBy}</Col>
                  </Row>
               </ColValue>
            </Row>
         ) : null}
         {reviewItem.machineSetupBy ? (
            <Row>
               <ColPrompt title="Machine Setup(s)" />
               <ColValue>
                  <Row>
                     <Col xs="6">
                        <span>Date:</span> {reviewItem.machineSetupDate}
                     </Col>
                     <Col xs="6">Completed By: {reviewItem.machineSetupBy}</Col>
                  </Row>
               </ColValue>
            </Row>
         ) : null}
         {reviewItem.bomSetupBy ? (
            <Row>
               <ColPrompt title="BOM Setup" />
               <ColValue>
                  <Row>
                     <Col xs="6">
                        <span>Date:</span> {reviewItem.bomSetupDate}
                     </Col>
                     <Col xs="6">Completed By: {reviewItem.bomSetupBy}</Col>
                  </Row>
               </ColValue>
            </Row>
         ) : null}
         {currentUserisReviewer &&
            ((specIsPM && !reviewItem.specSetupBy) ||
               (!specIsPM &&
                  (!reviewItem.bomSetupBy ||
                     !reviewItem.machineSetupBy ||
                     !reviewItem.specSetupBy))) ? (
            <Fragment>
               <Row>
                  <Col xxl={{ offset: 3, size: 6 }} style={{ color: "white" }}>
                     This item is not yet ready for final approval. Not all steps have
                     been completed.
                  </Col>
               </Row>
               {!!nextStep ? (
                  <Row className="mb-2">
                     <Col xxl={{ offset: 3, size: 6 }}>
                        <a href={nextStep.url} target="_blank" rel="noreferrer" className="styledAnchor">
                           {nextStep.prompt}
                        </a>
                     </Col>
                  </Row>
               ) : null}
            </Fragment>
         ) : null}
         {reviewItem.rejectedBy ? (
            <Row>
               <ColPrompt title="Rejected Details" />
               <ColValue>
                  <Row>
                     <Col xs="6">
                        <span>Date:</span>{" "}
                        {dateHelpers.toLongDateString(reviewItem.rejectDate)}
                     </Col>
                     <Col xs="6">
                        Rejected By: {reviewItem.rejectedBy}
                        <div className="text-white">{reviewItem.rejectComment}</div>
                     </Col>
                  </Row>
               </ColValue>
            </Row>
         ) : null}
         {reviewItem.approvedBy ? (
            <Row>
               <ColPrompt title="Final Approval" />
               <ColValue>
                  <Row>
                     <Col xs="6">
                        <span>Date:</span>{" "}
                        {dateHelpers.toLongDateString(reviewItem.approvedDate)}
                     </Col>
                     <Col xs="6">Completed By: {reviewItem.approvedBy}</Col>
                  </Row>
               </ColValue>
            </Row>
         ) : null}
         {reviewItem.isActive &&
            currentUserisReviewer &&
            ((specIsPM && reviewItem.specSetupDate !== "01/01/0001") ||
               (reviewItem.specSetupDate !== "01/01/0001" &&
                  reviewItem.bomSetupDate !== "01/01/0001" &&
                  reviewItem.machineSetupDate !== "01/01/0001")) ? (
            <Fragment>
               <Row>
                  <ColPrompt title="Final Approval" />
                  <ColValue>
                     <Button
                        title="Approve"
                        className="shredButton ms-2"
                        onClick={onApprove}
                     >
                        Approve
                     </Button>
                  </ColValue>
               </Row>
               <Row className="mt-2">
                  <ColPrompt title="OR" />
                  <ColValue>
                     <Button
                        title="Reject"
                        color="danger"
                        className="shredButton ms-2"
                        onClick={onReject}
                     >
                        Reject
                     </Button>
                     <Input
                        className="serviceThreadTextArea"
                        type="textarea"
                        value={rejectionReason}
                        maxLength={255}
                        style={{ height: "150px", marginTop: "4px" }}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Rejection Reason"
                     />
                  </ColValue>
               </Row>
            </Fragment>
         ) : null}
         <hr className="my-2" />
      </Fragment>
   );
}

export default function CreateNewProductionItem(props) {
   const { routeId } = useParams();
   const { currentUser } = useContext(UserContext);
   const navigate = useNavigate();
   const [currentUserIsReviewer, setCurrentUserIsReviewer] = useState(false);
   const [editItem, setEditItem] = useState(null);
   const [newItem, setNewItem] = useState({ itemComment: "" });
   const [refData, setRefData] = useState({});
   const [referenceItem, setReferenceItem] = useState(null);
   const [reviewItem, setReviewItem] = useState(null);
   const [working, setWorking] = useState(false);

   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(initialLoad, []);

   function getItemClasses() {
      return api
         .fetch("Reference/GetItemClassesForCreateItem")
         .then((response) => {
            if (response.data && response.data.success) {
               return {
                  ItemClasses: _.chain(response.data.itemClasses)
                     .map((opt) => ({
                        ...opt,
                        value: opt.itemClassCode,
                        label: opt.itemClassWithDesc,
                     }))
                     .sortBy((opt) => opt.label)
                     .value(),
               };
            } else {
               toast.error("Could not find item class list");
            }
         })
         .catch(api.catchHandler);
   }

   function getSiteList() {
      return api
         .fetch("Reference/GetSiteList")
         .then((response) => {
            if (response.data && response.data.success) {
               return {
                  SiteList: _.map(response.data.sites, (c) =>
                     Object.assign(c, {
                        label: c.siteWithDescription,
                        value: c.siteId,
                     })
                  ),
               };
            } else {
               toast.error("Could not find site list");
            }
         })
         .catch(api.catchHandler);
   }

   function getThreadColors() {
      return api
         .fetch("Reference/GetThreadColors")
         .then((response) => {
            if (response.data && response.data.success) {
               return {
                  ThreadColors: _.map(response.data.threadColors, (c) =>
                     Object.assign(c, { label: c.name, value: c.id })
                  ),
               };
            } else {
               toast.error("Could not find thread colors");
            }
         })
         .catch(api.catchHandler);
   }

   function initialLoad() {
      setWorking(true);
      Promise.all([getThreadColors(), getSiteList(), getItemClasses()])
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => Object.assign(aggResults, x));
            const { ThreadColors, SiteList, ItemClasses } = aggResults;
            setRefData({
               threadColors: ThreadColors,
               siteList: SiteList,
               itemClasses: ItemClasses,
            });
            if (routeId ? parseInt(routeId) : routeId) {
               const urlTokens = routeId.split("-");
               const requestId = urlTokens.length > 1 ? urlTokens[1] : routeId;
               api
                  .post("Item/GetProductionItemRequest", { requestId: requestId })
                  .then((r) => {
                     if (r.data && r.data.success) {
                        let item = r.data.message.reviewItem;
                        item.requestDate = dateHelpers.momentFromString(item.requestDate);
                        item.itemSetupDate = dateHelpers.toLongDateString(item.itemSetupDate);
                        item.specSetupDate = dateHelpers.toLongDateString(item.specSetupDate);
                        item.machineSetupDate = dateHelpers.toLongDateString(item.machineSetupDate);
                        item.bomSetupDate = dateHelpers.toLongDateString(item.bomSetupDate);
                        if (r.data.message.itemSetupComplete) {
                           setReviewItem(item);
                           // setCurrentUserIsReviewer(r.data.message.isReviewer);
                           setCurrentUserIsReviewer(true); // How to set this?
                        } else {
                           const productionItemData = r.data.message.referenceItem;
                           const tempItem = Object.assign(
                              {},
                              {
                                 id: item.id,
                                 abcCode: productionItemData?.abcCode
                                    ? productionItemData.abcCode
                                    : constants.ABC_CODE.A.value,
                                 binLocation: productionItemData?.binLocation,
                                 color: _.trim(productionItemData?.color),
                                 currentCost: _.round(productionItemData?.currentCost, 2),
                                 itemComment: item.itemComment,
                                 listPrice: productionItemData?.listPrice,
                                 minOrderQty: productionItemData?.realMinOrderQty,
                                 pkgType: productionItemData?.pkgType,
                                 referenceItem: item.referenceItem,
                                 requestBy: item.requestBy,
                                 requestDate: dateHelpers.toLongDateString(item.requestDate),
                                 shippingClass: _.trim(productionItemData?.shippingClass) || constants.SHIPPING_CLASS.A,
                                 siteId: productionItemData?.siteId || "PLT",
                                 standardCost: _.round(productionItemData?.standardCost, 2),
                                 standardNetWeight: _.trim(productionItemData?.standardNetWeight),
                                 tareWeight: _.trim(productionItemData?.tareWeight),
                                 unitsPerSku: _.trim(productionItemData?.unitsPerSku),
                              }
                           );
                           setEditItem(tempItem);
                           if (productionItemData?.itemNumber) {
                              setReferenceItem(productionItemData); // if the reference item is on the api then set it
                           }
                        }
                     } else {
                        toast.error("Could not load request id " + routeId);
                        console.error(r.data.message);
                     }
                  })
                  .catch(api.catchHandler);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   function onChange(field, value) {
      let changes = Object.assign({}, editItem);
      changes[field] = value;
      setEditItem(changes);
   }

   function onCreateItem(item, selectedItemClass, prePopulateSpecification, prePopulateMachineSetups) {
      let payload = Object.assign({}, item);
      payload.itemRequestId = payload.id;
      if (payload.abcCode) {
         payload.abcCode = payload.abcCode.value;
      }
      if (payload.vendorId) {
         payload.vendorId = payload.vendorId.value;
      }
      if (payload.vendorItem) {
         payload.vendorItemNumber = payload.vendorItem;
      }
      payload.itemClassIsPurchasedMaterial = selectedItemClass.isPurchasedMaterial;
      payload.itemClassIsInProcessBulk = selectedItemClass.isInProcess;
      payload.itemClassIsPackaging = selectedItemClass.isPkg;
      if (selectedItemClass.isPkg) {
         payload.packageType = payload?.pkgType
      }
      payload.itemClass = selectedItemClass.value;
      payload.referenceItemNumber = referenceItem?.itemNumber || "";
      delete payload.referenceItem;
      payload.prePopulateSpecification = prePopulateSpecification;
      payload.prePopulateMachineSetups = prePopulateMachineSetups;
      payload.listPrice = payload.listPrice ? parseFloat(payload?.listPrice) : 0;
      payload.vendorPrice = payload.vendorPrice
         ? parseFloat(payload?.vendorPrice)
         : 0;
      payload.standardCost = parseFloat(payload?.standardCost);

      if (!selectedItemClass.isFinishedGood) {
         payload.wasteFactor = parseFloat(payload?.wasteFactor / 100);
      }

      if (selectedItemClass.isFinishedGood) {
         if (
            _.includes(
               [payload.listPrice, payload.standardCost, payload.vendorPrice],
               NaN || undefined
            )
         ) {
            toast.error("payload invalid");
            return;
         }
      } else {
         if (
            _.includes(
               [
                  payload.wasteFactor,
                  payload.listPrice,
                  payload.standardCost,
                  payload.vendorPrice,
               ],
               NaN || undefined
            )
         ) {
            toast.error("payload invalid");
            return;
         }
      }
      setWorking(true);
      api
         .post("Item/CreateSalesInventoryItem", payload)
         .then((res) => {
            if (res && res.data && res.data.success) {
               toast.success("Your new production item request has been created");
               navigate(constants.ENDPOINTS.createItemCode);
            } else if (res.data?.message) {
               toast.warning(res.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   function onStartRequest() {
      const defaultProdItem = {
         abcCode: null,
         binLocation: "",
         color: "",
         currentCost: "",
         listPrice: "",
         minOrderQty: "",
         onStartRequest,
         pkgType: "",
         shippingClass: constants.SHIPPING_CLASS.A,
         siteId: "PLT",
         standardCost: "",
         standardNetWeight: "",
         tareWeight: "",
         unitsPerSku: "",
      };
      let warnings = [];
      // if (!referenceItem) {
      //   warnings.push("Part number is required\n");
      // }
      if (!newItem.itemComment) {
         warnings.push("Comment is required\n");
      }
      if (warnings.length) {
         toast.error(warnings.join(''));
         return;
      }
      setWorking(true);
      api
         .post("Item/InsertProductionItemRqst", {
            ReferenceItem: referenceItem?.itemNumber || "",
            ItemComment: newItem.itemComment,
         })
         .then((response) => {
            if (response.data.success) {
               const productionItemData = _.isNil(response.data.productionItemData.refItemInfo)
                  ? defaultProdItem
                  : response.data.productionItemData.refItemInfo;
               const tempItem = Object.assign({}, newItem, {
                  id: response.data.productionItemData.itemRequestId,
                  abcCode: productionItemData.abcCode
                     ? productionItemData.abcCode
                     : constants.ABC_CODE.A.value,
                  binLocation: productionItemData.binLocation,
                  color: _.trim(productionItemData.color) || "NATURAL",
                  currentCost: _.round(productionItemData.currentCost, 2),
                  itemComment: newItem.itemComment,
                  listPrice: productionItemData.listPrice,
                  minOrderQty: productionItemData.realMinOrderQty,
                  pkgType: productionItemData.pkgType,
                  referenceItem: referenceItem?.itemNumber || "",
                  shippingClass: _.trim(productionItemData.shippingClass) || constants.SHIPPING_CLASS.A,
                  siteId: productionItemData.siteId || "PLT",
                  standardCost: _.round(productionItemData.standardCost, 2),
                  standardNetWeight: _.trim(productionItemData.standardNetWeight),
                  tareWeight: _.trim(productionItemData.tareWeight),
                  unitsPerSku: _.trim(productionItemData.unitsPerSku),
               });
               setEditItem(tempItem);
               toast.success("Your item request has been created successfully!");
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   let createDate = null;
   let displayId = null;
   let requestByName = currentUser.fullName;
   if (editItem) {
      createDate = editItem.requestDate;
      displayId = `NPI-${editItem.id}`;
      requestByName = editItem.requestBy || requestByName;
   } else if (reviewItem) {
      createDate = dateHelpers.toLongDateString(reviewItem.requestDate);
      displayId = `NPI-${reviewItem.id}`;
      requestByName = reviewItem.requestBy || requestByName;
   } else {
      createDate = dateHelpers.currentDate();
   }

   if (reviewItem) {
      return (
         <DesktopContainer screenName="Production Item">
            <ServiceThreadSpinner on={working}>
               <div className="filter-header">
                  <Row>
                     <Col xs={{ offset: 2, size: 8 }}>
                        <RequestHeader
                           requestedByFullName={reviewItem.requestBy}
                           displayId={displayId}
                           createDate={createDate}
                        />
                        <ReviewRequest
                           currentUserisReviewer={currentUserIsReviewer}
                           reviewItem={reviewItem}
                           setReviewItem={setReviewItem}
                           setWorking={setWorking}
                        />
                     </Col>
                  </Row>
               </div>
               <AttachmentRow displayId={displayId} />
            </ServiceThreadSpinner>
         </DesktopContainer>
      );
   }
   return (
      <DesktopContainer screenName="Production Item">
         <ServiceThreadSpinner on={working}>
            <div className="filter-header">
               <Row className="justify-content-center">
                  {currentUser !== null && (
                     <Col xs={9}>
                        <RequestHeader
                           requestedByFullName={requestByName}
                           displayId={displayId}
                           createDate={createDate}
                        />
                        {editItem ? (
                           <>
                              <ItemDescriptionHeader
                                 itemWithDesc={referenceItem ? referenceItem.itemWithDesc : "Not Provided"}
                                 itemClass={editItem.itemClass}
                                 itemClasses={refData.itemClasses}
                                 onChange={onChange}
                                 comment={newItem?.itemComment}
                              />
                              {editItem.itemClass ? (
                                 <EditRequest
                                    referenceItem={referenceItem}
                                    editItem={editItem}
                                    siteList={refData.siteList}
                                    threadColors={refData.threadColors}
                                    onChange={onChange}
                                    onCreateItem={onCreateItem}
                                    setEditItem={setEditItem}
                                 />
                              ) : null}
                              <AttachmentRow displayId={displayId} />
                           </>
                        ) : (
                           <InitiateRequest
                              referenceItem={referenceItem}
                              setReferenceItem={setReferenceItem}
                              newItem={newItem}
                              setNewItem={setNewItem}
                              onStartRequest={onStartRequest}
                           />
                        )}
                     </Col>
                  )}
               </Row>
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
